import React, { useEffect } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import servicesContent from '../content/services.json';
import SectionCard from './SectionCard';

const Services = () => {
    const location = useLocation();

    const {
        title,
        intro,
        duration,
        fee,
        meetingFormat,
        servicesAsterisk,
        items,
        sessionExamplesSectionTitle,
        sessionExamplesIntro,
        pediatricSessionTitle,
        pediatricSessionSteps,
        adultSessionTitle,
        adultSessionSteps,
        sessionExamplesClosing,
        billingSectionTitle,
        billingCards,
        groupRateDiscountTitle,
        groupRateDiscountBody,
        fuelSurchargeTitle,
        fuelSurchargeBody,
    } = servicesContent;

    const indirectConsultationItem = items.find(
        (service) => service.title === 'Indirect Consultation'
    );

    const detailedItems = items.filter(
        (service) =>
            service.details?.trim() && service.title !== 'Indirect Consultation'
    );

    const compactItems = items.filter((service) => !service.details?.trim());

    const detailRowSx = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        mb: 1
    };

    const labelSx = {
        color: '#ff751f',
        fontWeight: 700,
        mr: 0.5
    };

    const valueSx = {
        color: 'rgba(0, 71, 110, 0.78)'
    };

    const sessionStepSx = {
        color: 'rgba(0, 71, 110, 0.78)',
        lineHeight: 1.8,
        mb: 1
    };

    const nestedPanelSx = {
        p: 2.5,
        borderRadius: 3,
        backgroundColor: '#f7deed',
        minWidth: 0,
        boxSizing: 'border-box',
        width: '100%',
        scrollMarginTop: { xs: '140px', md: '170px' }
    };

    const slugifyTitle = (text) =>
        text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');

    useEffect(() => {
        if (!location.hash) return;

        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);

        if (element) {
            window.setTimeout(() => {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
        }
    }, [location]);

    const renderServiceContent = (service) => (
        <>
            <Typography
                className="heading-font"
                variant="h5"
                sx={{ color: '#00476e', fontWeight: 700, mb: 2 }}
            >
                {service.title}
            </Typography>

            <Box sx={detailRowSx}>
                <Typography variant="body1" component="span" sx={labelSx}>
                    {duration}
                </Typography>
                <Typography variant="body1" component="span" sx={valueSx}>
                    {service.duration}
                </Typography>
            </Box>

            <Box sx={detailRowSx}>
                <Typography variant="body1" component="span" sx={labelSx}>
                    {meetingFormat}
                </Typography>
                <Typography variant="body1" component="span" sx={valueSx}>
                    {service.meetingFormat}
                </Typography>
            </Box>

            <Box sx={{ ...detailRowSx, mb: service.details ? 2 : 0 }}>
                <Typography variant="body1" component="span" sx={labelSx}>
                    {fee}
                </Typography>
                <Typography variant="body1" component="span" sx={valueSx}>
                    {service.fee}
                </Typography>
            </Box>

            {service.details && (
                <Typography
                    variant="body1"
                    sx={{
                        color: 'rgba(0, 71, 110, 0.78)',
                        lineHeight: 1.8
                    }}
                >
                    {service.details}
                </Typography>
            )}
        </>
    );

    const renderSessionContent = (title, steps) => (
        <>
            <Typography
                className="heading-font"
                variant="h5"
                sx={{ color: '#00476e', fontWeight: 700, mb: 2 }}
            >
                {title}
            </Typography>

            <Box component="ol" sx={{ pl: 3, m: 0 }}>
                {steps?.map((step, index) => (
                    <Box component="li" key={index} sx={{ mb: 1 }}>
                        <Typography variant="body1" sx={sessionStepSx}>
                            {step}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </>
    );

    const renderPolicyCardContent = (card) => (
        <>
            <Typography
                className="heading-font"
                variant="h5"
                sx={{ color: '#00476e', fontWeight: 700, mb: 2 }}
            >
                {card.title}
            </Typography>

            {card.paragraphs?.map((paragraph, index) => (
                <Typography
                    key={index}
                    variant="body1"
                    sx={{
                        color: 'rgba(0, 71, 110, 0.78)',
                        lineHeight: 1.8,
                        mb: index === card.paragraphs.length - 1 ? 0 : 2
                    }}
                >
                    {paragraph}
                </Typography>
            ))}
        </>
    );

    return (
        <Container
            maxWidth={false}
            sx={{
                py: { xs: 4, md: 7 },
                width: 'min(92vw, 1600px)',
                mx: 'auto'
            }}
        >
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <SectionCard sx={{ mb: 5 }}>
                    <Typography
                        className="heading-font"
                        variant="h2"
                        sx={{
                            color: '#00476e',
                            fontWeight: 700,
                            mb: 2,
                            fontSize: { md: '2.7rem' }
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(0, 71, 110, 0.82)',
                            lineHeight: 1.8,
                            maxWidth: "100%",
                            mb: 4
                        }}
                    >
                        {intro}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3
                        }}
                    >
                        {detailedItems.map((service) => (
                            <Box
                                key={service.title}
                                id={slugifyTitle(service.title)}
                                sx={nestedPanelSx}
                            >
                                {renderServiceContent(service)}
                            </Box>
                        ))}

                        {compactItems.length > 0 && (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 3,
                                    width: '100%'
                                }}
                            >
                                {compactItems.map((service) => (
                                    <Box
                                        key={service.title}
                                        id={slugifyTitle(service.title)}
                                        sx={{ ...nestedPanelSx, height: '100%' }}
                                    >
                                        {renderServiceContent(service)}
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {indirectConsultationItem && (
                            <Box
                                id={slugifyTitle(indirectConsultationItem.title)}
                                sx={nestedPanelSx}
                            >
                                {renderServiceContent(indirectConsultationItem)}
                            </Box>
                        )}

                        <Box sx={nestedPanelSx}>
                            <Typography
                                className="heading-font"
                                variant="h5"
                                sx={{ color: '#00476e', fontWeight: 700, mb: 2 }}
                            >
                                {groupRateDiscountTitle}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'rgba(0, 71, 110, 0.78)',
                                    lineHeight: 1.8
                                }}
                            >
                                {groupRateDiscountBody}
                            </Typography>
                        </Box>

                        <Box sx={nestedPanelSx}>
                            <Typography
                                className="heading-font"
                                variant="h5"
                                sx={{ color: '#00476e', fontWeight: 700, mb: 2 }}
                            >
                                {fuelSurchargeTitle}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'rgba(0, 71, 110, 0.78)',
                                    lineHeight: 1.8
                                }}
                            >
                                {fuelSurchargeBody}
                            </Typography>
                        </Box>
                    </Box>
                </SectionCard>

                <SectionCard sx={{ mb: 5 }}>
                    <Typography
                        className="heading-font"
                        variant="h3"
                        sx={{
                            color: '#00476e',
                            fontWeight: 700,
                            mb: 2,
                            fontSize: { md: '2.2rem' }
                        }}
                    >
                        {sessionExamplesSectionTitle}
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(0, 71, 110, 0.82)',
                            lineHeight: 1.8,
                            mb: 3
                        }}
                    >
                        {sessionExamplesIntro}
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 3,
                            width: '100%'
                        }}
                    >
                        <Box sx={nestedPanelSx}>
                            {renderSessionContent(pediatricSessionTitle, pediatricSessionSteps)}
                        </Box>

                        <Box sx={nestedPanelSx}>
                            {renderSessionContent(adultSessionTitle, adultSessionSteps)}
                        </Box>
                    </Box>

                    {sessionExamplesClosing && (
                        <Typography
                            variant="body1"
                            sx={{
                                mt: 3,
                                color: 'rgba(0, 71, 110, 0.78)',
                                lineHeight: 1.8
                            }}
                        >
                            {sessionExamplesClosing}
                        </Typography>
                    )}
                </SectionCard>

                <SectionCard>
                    <Typography
                        className="heading-font"
                        variant="h3"
                        sx={{
                            color: '#00476e',
                            fontWeight: 700,
                            mb: 3,
                            fontSize: { md: '2.2rem' }
                        }}
                    >
                        {billingSectionTitle}
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 3,
                            width: '100%'
                        }}
                    >
                        {billingCards?.map((card) => (
                            <Box key={card.title} sx={nestedPanelSx}>
                                {renderPolicyCardContent(card)}
                            </Box>
                        ))}
                    </Box>
                </SectionCard>
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <SectionCard sx={{ mb: 4 }}>
                    <Typography
                        className="heading-font"
                        variant="h2"
                        sx={{
                            color: '#00476e',
                            fontWeight: 700,
                            mb: 2,
                            fontSize: '2rem'
                        }}
                    >
                        Services
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(0, 71, 110, 0.82)',
                            lineHeight: 1.8
                        }}
                    >
                        {intro}
                    </Typography>
                </SectionCard>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3
                    }}
                >
                    {detailedItems.map((service) => (
                        <SectionCard key={service.title} id={slugifyTitle(service.title)}>
                            {renderServiceContent(service)}
                        </SectionCard>
                    ))}

                    {compactItems.map((service) => (
                        <SectionCard key={service.title} id={slugifyTitle(service.title)}>
                            {renderServiceContent(service)}
                        </SectionCard>
                    ))}

                    {indirectConsultationItem && (
                        <SectionCard id={slugifyTitle(indirectConsultationItem.title)}>
                            {renderServiceContent(indirectConsultationItem)}
                        </SectionCard>
                    )}

                    <SectionCard>
                        <Typography
                            className="heading-font"
                            variant="h5"
                            sx={{ color: '#00476e', fontWeight: 700, mb: 2 }}
                        >
                            {groupRateDiscountTitle}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(0, 71, 110, 0.78)',
                                lineHeight: 1.8
                            }}
                        >
                            {groupRateDiscountBody}
                        </Typography>
                    </SectionCard>

                    <SectionCard>
                        <Typography
                            className="heading-font"
                            variant="h5"
                            sx={{ color: '#00476e', fontWeight: 700, mb: 2 }}
                        >
                            {fuelSurchargeTitle}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(0, 71, 110, 0.78)',
                                lineHeight: 1.8
                            }}
                        >
                            {fuelSurchargeBody}
                        </Typography>
                    </SectionCard>
                </Box>

                {servicesAsterisk && (
                    <Typography
                        variant="body2"
                        sx={{
                            mt: 3,
                            color: 'rgba(0, 71, 110, 0.72)',
                            fontStyle: 'italic',
                            lineHeight: 1.7
                        }}
                    >
                        {servicesAsterisk}
                    </Typography>
                )}

                <SectionCard sx={{ mt: 5, mb: 4 }}>
                    <Typography
                        className="heading-font"
                        variant="h3"
                        sx={{
                            color: '#00476e',
                            fontWeight: 700,
                            mb: 2,
                            fontSize: '1.7rem'
                        }}
                    >
                        {sessionExamplesSectionTitle}
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(0, 71, 110, 0.82)',
                            lineHeight: 1.8
                        }}
                    >
                        {sessionExamplesIntro}
                    </Typography>
                </SectionCard>

                <SectionCard sx={{ mb: 3 }}>
                    {renderSessionContent(pediatricSessionTitle, pediatricSessionSteps)}
                </SectionCard>

                <SectionCard sx={{ mb: 5 }}>
                    {renderSessionContent(adultSessionTitle, adultSessionSteps)}
                </SectionCard>

                <SectionCard sx={{ mb: 4 }}>
                    <Typography
                        className="heading-font"
                        variant="h3"
                        sx={{
                            color: '#00476e',
                            fontWeight: 700,
                            mb: 2,
                            fontSize: '1.7rem'
                        }}
                    >
                        {billingSectionTitle}
                    </Typography>
                </SectionCard>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3
                    }}
                >
                    {billingCards?.map((card) => (
                        <SectionCard key={card.title}>
                            {renderPolicyCardContent(card)}
                        </SectionCard>
                    ))}
                </Box>

                {sessionExamplesClosing && (
                    <Typography
                        variant="body1"
                        sx={{
                            mt: 3,
                            color: 'rgba(0, 71, 110, 0.78)',
                            lineHeight: 1.8
                        }}
                    >
                        {sessionExamplesClosing}
                    </Typography>
                )}
            </Box>
        </Container>
    );
};

export default Services;